/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

// NOTE: This entity selector does not support Tabular Data at this time.
class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	getTextInputHTMLID(pInputHTMLID)
	{
		return `#ENTITY-DISPLAY-FOR-${pInputHTMLID}`;
	}

	getTabularTextInputID(pInputHTMLID, pRowIndex)
	{
		return `#ENTITY-DISPLAY-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean}
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, null, pInput, pValue, pHTMLSelector, pRowIndex).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} [pRowIndex] - The row index parameter.
	 * @return {Promise<boolean>}
	 */
	async _onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		const inputMethod = pRowIndex != null ? 'getTabularTextInputID' : 'getTextInputHTMLID';
		// Try to get the input element before we do anything else
		const tmpInputTextElements = this.pict.ContentAssignment.getElement(this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex));
		if (!tmpInputTextElements || tmpInputTextElements.length < 1)
		{
			return false;
		}
		/** @type {HTMLInputElement} */
		const tmpInputTextElement = tmpInputTextElements[0];
		// if we have a value (ID), go fetch the Entity for display (Name, etc.)
		if (pValue)
		{
			const { Record: tmpEntity, DisplayName: tmpDisplayName } = await this.pict.providers.Select2Util.getEntityWithDisplayAsync(pInput.PictForm.Entity, pValue,
			{
				IdentityField: pInput.PictForm.IdentityField || null,
				JoinEntity: pInput.PictForm.JoinEntity,
				JoinEntityFirst: pInput.PictForm.JoinEntityFirst ?? true,
				EntityDisplayField: pInput.PictForm.EntityDisplayField,
				JoinEntityDisplayField: pInput.PictForm.JoinEntityDisplayField,
				JoinField: pInput.PictForm.JoinField ? pInput.PictForm.JoinField : pInput.PictForm.JoinEntity ? `ID${ pInput.PictForm.JoinEntity }` : null,
				EntityTagLast: pInput.PictForm.EntityTag && pInput.PictForm.EntityTagLast,
			});
			// if we have an EntityTag, display that as a tag along with the Name
			if (pInput.PictForm.EntityTag)
			{
				let tmpDisplayTemplate = '';
				if (tmpInputTextElement.tagName === 'DIV')
				{
					const tmpEntityTag = tmpEntity[pInput.PictForm.EntityTag];
					const tmpTaggedName = tmpDisplayName || (`Unnamed ${ pInput.PictForm.Entity } #` + tmpEntity[`ID${ pInput.PictForm.Entity }`]);
					// A configured-but-empty tag paints no badge, rendering exactly as the untagged case
					// below does. An EntityTag naming a field the record does not carry (or carries blank)
					// used to invent an "N/A" pill here that the edit selector never showed, so an author
					// only ever saw it in the PDF. A numeric 0 is a real tag and still badges.
					tmpDisplayTemplate = (tmpEntityTag === undefined || tmpEntityTag === null || tmpEntityTag === '') ?
						tmpTaggedName :
						this.pict.providers.Select2Util.buildSelect2CompoundDisplayTemplate(
							tmpEntityTag,
							tmpTaggedName,
							false,
							tmpEntity.EntityTagLast,
						);
				}
				else
				{
					tmpDisplayTemplate = pValue || (`Unnamed ${ pInput.PictForm.Entity } #` + tmpEntity[`ID${ pInput.PictForm.Entity }`]);
				}
				this.pict.ContentAssignment.assignContent(tmpInputTextElement, tmpDisplayTemplate);
			}
			else
			{
				this.pict.ContentAssignment.assignContent(tmpInputTextElement, tmpDisplayName);
			}
		}
		else
		{
			this.pict.ContentAssignment.assignContent(tmpInputTextElement, '');
		}
	}
}

module.exports = CustomInputHandler;
